// ===========================================
// MAKE PLAN — for brands that sell through wholesale
// ===========================================
// A standalone CPG has no Shopify feed: demand IS the open purchase orders.
// This computes what to make from three things the account already maintains:
//   open wholesale orders (cases owed) − finished goods on hand (inventory
//   items named exactly like the product) ÷ batch recipe yield → batches.
// Every number is traceable to a screen the operator can open.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Factory, ArrowRight } from 'lucide-react';
import api, { wholesaleApi, menuApi, recipesApi, inventoryApi } from '../services/api';

const asList = (d: any, key: string) => (Array.isArray(d) ? d : d?.[key] || []);

export default function WholesaleMakePlan() {
  const qc = useQueryClient();
  const [runFor, setRunFor] = useState<any>(null);           // recipe being logged
  const [runBatches, setRunBatches] = useState(1);
  const [runResult, setRunResult] = useState<any>(null);      // last run summary
  const [editStock, setEditStock] = useState<{ name: string; itemId: string | null; value: string } | null>(null);
  const saveStock = useMutation({
    mutationFn: async ({ itemId, name, value }: { itemId: string | null; name: string; value: number }) => {
      if (itemId) return (await api.patch(`/v1/inventory/${itemId}`, { currentQuantity: value })).data;
      return (await api.post('/v1/inventory', { name, unit: 'case', category: 'finished goods', unitCost: 0, currentQuantity: value, parLevel: 0, reorderPoint: 0, reorderQuantity: 0 })).data;
    },
    onSuccess: () => { setEditStock(null); qc.invalidateQueries({ queryKey: ['inventory-makeplan'] }); },
  });
  const logRun = useMutation({
    mutationFn: async ({ recipeId, batches }: { recipeId: string; batches: number }) =>
      (await api.post('/v1/production/runs', { recipeId, batches })).data?.data,
    onSuccess: (data) => {
      setRunResult(data); setRunFor(null);
      qc.invalidateQueries({ queryKey: ['inventory-makeplan'] });
      qc.invalidateQueries({ queryKey: ['recipes-makeplan'] });
    },
  });
  const { data: ordersData } = useQuery({ queryKey: ['wholesale-orders-makeplan'], queryFn: async () => (await wholesaleApi.getOrders()).data?.data || [] });
  const { data: menuData } = useQuery({ queryKey: ['menu-items-makeplan'], queryFn: async () => { const r = await menuApi.getItems(); return r.data?.data || r.data || []; }, staleTime: 60_000 });
  const { data: recipesData } = useQuery({ queryKey: ['recipes-makeplan'], queryFn: async () => { const r = await recipesApi.getAll(); return r.data?.data || r.data || []; }, staleTime: 60_000 });
  const { data: invData } = useQuery({ queryKey: ['inventory-makeplan'], queryFn: async () => { const r = await inventoryApi.getItems(); return r.data?.data || r.data || []; }, staleTime: 60_000 });

  const orders: any[] = asList(ordersData, 'orders');
  const products: any[] = asList(menuData, 'items');
  const recipes: any[] = asList(recipesData, 'recipes');
  const inventory: any[] = asList(invData, 'items');

  // Cases owed: orders that exist but haven't shipped yet. Once picked up the
  // goods are made by definition, so open is the only stage that creates work.
  const owed = new Map<string, number>();
  const owedOrders: any[] = [];
  for (const o of orders) {
    if (o.status === 'cancelled' || o.onConsignment) continue;
    if (o.pickedUp || o.freightDelivered || o.invoiceSent || o.paymentReceived) continue;
    owedOrders.push(o);
    for (const it of o.items || []) {
      if (!it.menuItemId) continue;
      owed.set(it.menuItemId, (owed.get(it.menuItemId) || 0) + (parseFloat(String(it.quantity)) || 0));
    }
  }

  // Finished goods on hand: an inventory item named exactly like the product.
  const onHandByName = new Map<string, number>(inventory.map((i: any) => [String(i.name), parseFloat(String(i.currentQuantity)) || 0]));
  const invIdByName = new Map<string, string>(inventory.map((i: any) => [String(i.name), i.id]));
  const recipeByMenuItem = new Map<string, any>(recipes.filter((r: any) => r.menuItemId).map((r: any) => [r.menuItemId, r]));

  const rows = products
    .filter((m: any) => owed.has(m.id))
    .map((m: any) => {
      const need = owed.get(m.id) || 0;
      const onHand = onHandByName.get(m.name) ?? null; // null = no finished-goods item tracked
      const make = Math.max(0, need - (onHand || 0));
      const recipe = recipeByMenuItem.get(m.id);
      const yieldPer = recipe ? parseFloat(String(recipe.yield)) || 0 : 0;
      const batches = make > 0 && yieldPer > 0 ? Math.ceil(make / yieldPer) : 0;
      return { id: m.id, name: m.name, need, onHand, make, recipe, batches, invId: invIdByName.get(m.name) || null };
    })
    .sort((a, b) => b.make - a.make || b.need - a.need);


  const onHandControl = (r: any) => {
    const es = editStock;
    if (es && es.name === r.name) return (
      <span className="inline-flex items-center gap-1">
        <input autoFocus type="number" min="0" value={es.value}
          onChange={(e) => setEditStock({ ...es, value: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') saveStock.mutate({ itemId: es.itemId, name: r.name, value: parseFloat(es.value) || 0 }); if (e.key === 'Escape') setEditStock(null); }}
          className="w-16 input text-right text-sm py-1" />
        <button onClick={() => saveStock.mutate({ itemId: es.itemId, name: r.name, value: parseFloat(es.value) || 0 })} disabled={saveStock.isPending} className="text-primary-600 text-xs font-medium">{saveStock.isPending ? '…' : 'Save'}</button>
      </span>
    );
    return (
      <button onClick={() => setEditStock({ name: r.name, itemId: r.invId, value: String(r.onHand ?? 0) })}
        className="text-slate-600 underline decoration-dotted underline-offset-2 hover:text-primary-700" title="Tap to correct the count">
        {r.onHand == null ? 'set count' : Math.floor(r.onHand).toLocaleString('en-US')}
      </button>
    );
  };

  if (owedOrders.length === 0) {
    return (
      <div className="card p-10 text-center text-slate-500">
        <Factory className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p>Nothing to make — no open purchase orders right now.</p>
        <p className="text-xs text-slate-400 mt-1">New POs on the <Link to="/wholesale" className="text-primary-600 font-medium">wholesale board</Link> appear here as cases to produce.</p>
      </div>
    );
  }

  const totalMake = rows.reduce((s, r) => s + r.make, 0);
  const totalBatches = rows.reduce((s, r) => s + r.batches, 0);

  return (
    <div className="space-y-4">
      <div className="card p-5 bg-primary-50 border-primary-100">
        <p className="text-lg md:text-xl font-bold text-slate-900">
          {totalMake > 0
            ? <>Make {Math.ceil(totalMake).toLocaleString('en-US')} cases{totalBatches > 0 && <span className="text-slate-500 font-semibold"> — {totalBatches} batch{totalBatches === 1 ? '' : 'es'}</span>}</>
            : <>You&apos;re covered — stock on hand fills every open order</>}
        </p>
        <p className="text-sm text-slate-600 mt-0.5">
          From {owedOrders.length} open purchase order{owedOrders.length === 1 ? '' : 's'} ({owedOrders.map((o) => o.poNumber || o.orderNumber).join(', ')})
        </p>
      </div>

      <div className="card overflow-x-auto hidden sm:block">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2.5">Product</th>
              <th className="px-4 py-2.5 text-right">Cases owed</th>
              <th className="px-4 py-2.5 text-right">On hand</th>
              <th className="px-4 py-2.5 text-right">To make</th>
              <th className="px-4 py-2.5 text-right">Batches</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 last:border-0">
                <td className="px-4 py-3 text-slate-900 font-medium">{r.name}</td>
                <td className="px-4 py-3 text-right text-slate-700">{Math.ceil(r.need).toLocaleString('en-US')}</td>
                <td className="px-4 py-3 text-right text-slate-500">{onHandControl(r)}</td>
                <td className={`px-4 py-3 text-right font-semibold ${r.make > 0 ? 'text-slate-900' : 'text-emerald-600'}`}>{r.make > 0 ? Math.ceil(r.make).toLocaleString('en-US') : 'covered'}</td>
                <td className="px-4 py-3 text-right text-slate-700">
                  {r.recipe
                    ? <button onClick={() => { setRunFor(r.recipe); setRunBatches(Math.max(1, r.batches)); setRunResult(null); }}
                        className="text-primary-600 font-medium hover:underline" title={`${r.recipe.name} yields ${r.recipe.yield} per run — tap to log a run`}>
                        {r.batches > 0 ? `${r.batches} × ` : ''}{r.recipe.name.replace(/ \(.*\)$/, '')} · log run
                      </button>
                    : r.make > 0 ? <span className="text-slate-400" title="Link a batch recipe to this product to get batch counts">—</span> : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phone layout: one card per product, nothing clipped off-screen */}
      <div className="sm:hidden space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="card p-4">
            <p className="font-semibold text-slate-900 text-sm mb-1.5">{r.name.replace(/ — .*$/, '')}</p>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-500">owed <span className="font-semibold text-slate-900">{Math.ceil(r.need)}</span></span>
              <span className="text-slate-500">on hand {onHandControl(r)}</span>
              <span className={r.make > 0 ? 'font-bold text-slate-900' : 'font-semibold text-emerald-600'}>{r.make > 0 ? `make ${Math.ceil(r.make)}` : 'covered'}</span>
            </div>
            {r.recipe && (
              <button onClick={() => { setRunFor(r.recipe); setRunBatches(Math.max(1, r.batches)); setRunResult(null); }}
                className="w-full btn btn-secondary text-sm py-2">
                {r.batches > 0 ? `Log ${r.batches} batch${r.batches === 1 ? '' : 'es'}` : 'Log a run'} — {r.recipe.name.replace(/ \(.*\)$/, '')}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* New demand enters as an order — keep that door obvious */}
      <div className="flex flex-wrap gap-2">
        <Link to="/wholesale?tab=orders" className="btn btn-secondary text-sm">+ Add an order</Link>
        <Link to="/wholesale?tab=orders&scan=1" className="btn btn-secondary text-sm">Scan a PO</Link>
      </div>

      {runResult && (
        <div className="card p-4 bg-emerald-50 border-emerald-100">
          <p className="font-semibold text-emerald-900">Run logged — {runResult.produced ? `${Math.round(runResult.produced.quantity)} ${runResult.produced.quantity === 1 ? 'case' : 'cases'} of ${runResult.produced.name.replace(/ — .*$/, '')} into stock` : runResult.recipe}</p>
          {(runResult.consumed || []).length > 0 && (
            <p className="text-xs text-emerald-800 mt-1">
              Used {runResult.consumed.map((c: any) => `${c.quantity} ${c.unit} ${c.name}${c.short ? ' (ran short!)' : ''}`).join(' · ')}
            </p>
          )}
        </div>
      )}

      {runFor && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-end sm:items-center justify-center p-4" onClick={() => setRunFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 pb-safe" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-900 mb-1">Log a production run</h3>
            <p className="text-sm text-slate-500 mb-4">{runFor.name} — each run yields {runFor.yield} {runFor.yieldUnit || 'cases'}</p>
            <div className="flex items-center justify-center gap-4 mb-4">
              <button onClick={() => setRunBatches((b) => Math.max(1, b - 1))} className="w-11 h-11 rounded-full border border-slate-200 text-xl text-slate-600 active:scale-95">−</button>
              <div className="text-center w-24">
                <p className="text-3xl font-bold text-slate-900">{runBatches}</p>
                <p className="text-xs text-slate-400">batch{runBatches === 1 ? '' : 'es'} · {Math.round((parseFloat(String(runFor.yield)) || 0) * runBatches)} {runFor.yieldUnit || 'cases'}</p>
              </div>
              <button onClick={() => setRunBatches((b) => b + 1)} className="w-11 h-11 rounded-full border border-slate-200 text-xl text-slate-600 active:scale-95">+</button>
            </div>
            <p className="text-[11px] text-slate-400 mb-4 text-center">Finished stock goes up; ingredients draw down through the recipe.</p>
            <div className="flex gap-2">
              <button onClick={() => setRunFor(null)} className="btn btn-secondary flex-1">Cancel</button>
              <button onClick={() => logRun.mutate({ recipeId: runFor.id, batches: runBatches })} disabled={logRun.isPending}
                className="btn btn-primary flex-1">{logRun.isPending ? 'Logging…' : 'Log run'}</button>
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-400 flex items-center gap-1 flex-wrap">
        Cases owed come from <Link to="/wholesale" className="text-primary-600">open POs</Link>; on-hand from finished goods in <Link to="/inventory" className="text-primary-600">Inventory</Link> (an item named like the product); batch sizes from <Link to="/recipes" className="text-primary-600">Recipes</Link>. <ArrowRight className="w-3 h-3 inline" /> Nothing here is estimated.
      </p>
    </div>
  );
}
